<template>
  <b-form ref="form" lazy-validation novalidate class="form-view col-xl-8" @submit.prevent="submit">
    <b-alert v-if="error" variant="danger">
      {{ error }}
    </b-alert>
    <b-row class="mb-4">
      <b-col>
        <h2 class="mq">{{ label }}</h2>
      </b-col>
      <b-col class="text-right">
        <b-button v-if="$route.name !== 'adminNewUser'" variant="rounded" @click="editLicenceAreas">
          Licence Areas assignment
        </b-button>
      </b-col>
    </b-row>
    <form-field-template
      v-for="textField in textFields"
      :key="`${textField.inputName}${textField.type}`"
      :validator="$v.userData[textField.inputName]"
      :label="textField.label"
      class="form-group--text"
    >
      <b-form-input
        :ref="textField.inputName"
        v-model="userData[textField.inputName]"
        :placeholder="textField.placeholder"
        :type="textField.type"
        @input="handleOnInput(textField.inputName)"
      />
      <ValidationMessage
        v-if="!!errors[textField.inputName]"
        :message="errors[textField.inputName]"
        class="form-group__message--show"
      />
    </form-field-template>
    <form-field-template label="" class="form-group--checkbox">
      <b-form-checkbox
        :ref="checkboxFields[0].inputName"
        v-model="userData.notifications[checkboxFields[0].inputName]"
        :disabled="isEmailCheckboxDisabled"
      >
        {{ checkboxFields[0].label }}
      </b-form-checkbox>
    </form-field-template>
    <form-field-template label="" class="form-group--checkbox">
      <b-form-checkbox
        :ref="checkboxFields[1].inputName"
        v-model="userData.notifications[checkboxFields[1].inputName]"
        :disabled="userData.phoneNumber === '' || $v.userData.phoneNumber.$error"
      >
        {{ checkboxFields[1].label }}
      </b-form-checkbox>
    </form-field-template>
    <form-field-template :validator="$v.userData.role" label="Role">
      <v-select
        v-model="userData.role"
        :searchable="false"
        :options="roles"
        class="custom-select"
      />
      <ValidationMessage
        v-if="!!errors.role"
        :message="errors.role"
        class="form-group__message--show"
      />
    </form-field-template>
    <template v-if="userData.role !== superadminRole">
      <form-field-template
        v-for="textField in privilegesScopeField"
        :key="`${textField.inputName}${textField.type}`"
        :validator="$v.userData[textField.inputName]"
        :label="textField.label"
        class="form-group--text"
      >
        <b-form-input
          :ref="textField.inputName"
          v-model="userData[textField.inputName]"
          :placeholder="textField.placeholder"
          :type="textField.type"
          @input="$v.userData[textField.inputName].$touch()"
        />
        <ValidationMessage
          v-if="!!errors[textField.inputName]"
          :message="errors[textField.inputName]"
          class="form-group__message--show"
        />
      </form-field-template>
    </template>
    <form-field-template label="" class="form-group--checkbox">
      <b-form-checkbox
        v-if="userData.role !== superadminRole"
        :ref="checkboxFields[2].inputName"
        v-model="userData[checkboxFields[2].inputName]"
      >
        {{ checkboxFields[2].label }}
      </b-form-checkbox>
    </form-field-template>
    <b-form-group class="form-group__buttons">
      <b-button v-if="onCancel" variant="link" class="font-weight-bold" @click.prevent="onCancel">
        Cancel
      </b-button>
      <b-button type="submit" :disabled="$v.userData.$invalid" variant="rounded">
        Submit
      </b-button>
    </b-form-group>
  </b-form>
</template>

<script>
import { required, email } from 'vuelidate/lib/validators';
import merge from 'lodash/merge';
import reduce from 'lodash/reduce';
import omit from 'lodash/omit';
import snakecaseKeys from 'snakecase-keys';
import Users from '@/resources/users';
import FormFieldTemplate from '@/components/shared/form-field-template';
import FormMixins from '@/components/mixins/form-mixins';

const validPhoneNumber = v => /^(\+\d{7,15})?$/.test(v || '');

export default {
  name: 'UsersForm',
  components: { FormFieldTemplate },
  mixins: [FormMixins],
  props: {
    onSuccess: {
      type: Function
    },
    resource: Object,
    label: String,
    userId: String,
    user: {
      email: String,
      username: String,
      role: String,
      phoneNumber: String,
      notifications: Array,
      privilegesScope: String,
      assignedAllLicenceAreas: Boolean,
      downloadAccess: Boolean
    }
  },
  data() {
    const SUPERADMIN_ROLE = 'superadmin';
    const notificationsObj = {};
    const user = this.user ? { ...this.user } : {};
    const userData = omit(
      merge(
        {},
        {
          email: '',
          username: '',
          phoneNumber: '',
          role: '',
          privilegesScope: '',
          notifications: [],
          downloadAccess: false,
          backendErrors: {}
        },
        user
      ),
      'id'
    );

    userData.notifications.forEach(notification => {
      notificationsObj[notification] = true;
    });

    userData.notifications = notificationsObj;

    return merge(
      {},
      {
        error: '',
        errors: {},
        superadminRole: SUPERADMIN_ROLE,
        defaultRoles: ['admin', 'user', 'engineer'],
        userData
      }
    );
  },
  validations() {
    const validators = {
      userData: {}
    };

    this.fields.forEach(field => {
      if (field.rules) {
        validators.userData[field.inputName] = field.rules;
      }
    });

    return validators;
  },
  computed: {
    roles() {
      if (this.$store.state.auth.role === this.superadminRole) {
        return [this.superadminRole, ...this.defaultRoles];
      }

      return this.defaultRoles;
    },
    fields() {
      return [
        {
          label: 'Username',
          inputName: 'username',
          type: 'text',
          group: 'text',
          rules: {
            required
          }
        },
        {
          label: 'Phone Number',
          inputName: 'phoneNumber',
          type: 'text',
          group: 'text',
          placeholder: 'e.g. +441234567890',
          rules: {
            phoneNumber: validPhoneNumber
          }
        },
        {
          label: 'E-mail',
          inputName: 'email',
          type: 'text',
          group: 'text',
          rules: {
            email
          }
        },
        {
          label: 'Password',
          inputName: 'password',
          group: 'text',
          type: 'password',
          rules: {
            password: v => !!this.userId || !!v
          }
        },
        {
          label: 'E-mail notification',
          inputName: 'email',
          type: 'checkbox'
        },
        {
          label: 'SMS notification',
          inputName: 'sms',
          type: 'checkbox'
        },
        {
          label: 'Role',
          inputName: 'role',
          type: 'select',
          options: this.roles,
          rules: {
            required
          }
        },
        {
          label: 'Privileges Scope',
          inputName: 'privilegesScope',
          type: 'text',
          group: 'privilegesScope',
          rules: {
            required,
            privilegesScope: v => {
              if (this.userData.role !== this.superadminRole) {
                return /^(\/\w+)+\/((\w+|#)\/)?$/.test(v || '');
              }
              return true;
            }
          }
        },
        {
          label: 'Access to downloading files',
          inputName: 'downloadAccess',
          type: 'checkbox'
        }
      ];
    },
    textFields() {
      return this.fields.filter(field => field.group === 'text');
    },
    checkboxFields() {
      return this.fields.filter(field => field.type === 'checkbox');
    },
    privilegesScopeField() {
      return this.fields.filter(field => field.group === 'privilegesScope');
    },
    isEmailCheckboxDisabled() {
      const { email } = this.userData;

      return !email || !email.length || this.$v.userData.email.$error;
    }
  },
  watch: {
    user(val) {
      return { ...val };
    },
    'userData.role': function userRole(role) {
      this.togglePrivilegesScope(role);
    }
  },
  methods: {
    togglePrivilegesScope(role) {
      this.userData.privilegesScope = role === this.superadminRole ? '/#/' : '';
    },
    onCancel() {
      this.$router.push({ name: 'adminUsersList' });
    },
    preventSendingEmptyEmailAddress(data) {
      if (data.email === '') {
        delete data.email; /* eslint no-param-reassign: "off" */
      }

      return data;
    },
    prepareDataToSend(data) {
      const cloneData = { ...data };
      const dataToSend = this.preventSendingEmptyEmailAddress(cloneData);

      dataToSend.notifications = reduce(
        dataToSend.notifications,
        (trues, v, k) => {
          if (v === true) {
            trues.push(k);
          }
          return trues;
        },
        []
      );

      return snakecaseKeys(dataToSend);
    },
    toggleCheckedNotification(checkboxFieldName, field, hasErrors) {
      if (field === '' || hasErrors) {
        this.$set(this.userData.notifications, checkboxFieldName, false);
      }
    },
    handleOnInput(fieldName) {
      const field = this.$v.userData[fieldName];
      field.$touch();

      if (fieldName === 'email') {
        this.toggleCheckedNotification('email', this.userData[fieldName], field.$error);
      } else if (fieldName === 'phoneNumber') {
        this.toggleCheckedNotification('sms', this.userData[fieldName], field.$error);
      }
    },
    submit() {
      const data = this.prepareDataToSend(this.userData);
      let request;
      this.errors = {};

      if (this.userId) {
        request = Users.update({ id: this.userId }, data);
      } else {
        request = Users.save({}, data);
      }

      request.then(this.onSuccess, this.handleRequestError).finally(() => {
        this.loading = false;
      });
    },
    editLicenceAreas() {
      this.$store.commit('users/editUser', this.user);
      this.$router.push({
        name: 'licenceAreasList',
        params: {
          user: this.user
        }
      });
    }
  }
};
</script>
